// Slice 3 client: descriptors, MYP judgements, feedback drafts.
// Shapes mirror src/teacher/{descriptors,judgement,feedback}.ts.

import type { Confidence } from './slice2';

export const BANDS = ['1-2', '3-4', '5-6', '7-8'] as const;

export interface DescriptorRow { band: string; strand: string; text: string }
export interface Descriptors {
  current_version: number; criterion: string; year_level: number; locked: boolean;
  head: { created_by: string; created_at: string; copied_from: string | null } | null;
  rows: DescriptorRow[]; strands: string[];
  versions: { version: number; created_by: string; created_at: string; copied_from: string | null }[];
  copyable: { id: string; title: string; version: number; rows: DescriptorRow[] }[];
}

export interface StrandEvidence { strand: string; band: string; evidence: string[] }
export type ProposalStatus = 'pending' | 'accepted' | 'superseded' | 'stale';
export interface JudgementRowView {
  student_id: string; name: string; student_code: string | null; class_name: string; attendance: 'present' | 'absent' | 'catchup' | 'exempt'; identity_status: string;
  evidence_revision: number; ready: boolean; reasons: string[];
  proposal: {
    id: string; status: ProposalStatus; ai_level: number | null; insufficient_reason: string | null; strand_evidence: StrandEvidence[];
    limiting_evidence: string | null; rationale: string | null; confidence: Confidence; review_reason: string | null; stale: string | null;
  } | null;
  accept_block: string | null;
  judgement: {
    teacher_level: number | null; teacher_note: string | null; status: 'draft' | 'confirmed' | 'needs_review'; judgement_revision: number;
    confirmed_by: string | null; confirmed_at: string | null; source: 'ai' | 'teacher'; basis_stale: string | null;
    disposition?: 'level' | 'not_assessable'; disposition_reason?: string | null;
  } | null;
}
export interface JudgementsView {
  assessment: { id: string; title: string; criterion: string; year_level: number; current_descriptor_version: number; judging_locked: boolean };
  descriptor_problem: string | null;
  rows: JudgementRowView[];
}

export type FbField = 'strength' | 'improvement' | 'next_step' | 'target';
export const FB_FIELDS: { id: FbField; label: string }[] = [
  { id: 'strength', label: 'Strength' }, { id: 'improvement', label: 'Improvement area' },
  { id: 'next_step', label: 'Actionable next step' }, { id: 'target', label: 'MYP progression target' },
];
export interface FeedbackRowView {
  student_id: string; name: string; class_name: string; attendance: string; ready: boolean; reasons: string[];
  judgement: { teacher_level: number | null; status: string; judgement_revision: number } | null;
  proposal: ({ id: string; status: ProposalStatus; field_refs: Record<FbField, string[]>; field_reviews: Partial<Record<FbField, string>>;
    confidence: Confidence; review_reason: string | null; stale: string | null; viewed: boolean } & Record<FbField, string | null>) | null;
  feedback: ({ source: 'ai' | 'teacher'; status: 'draft' | 'approved' | 'needs_review' | 'not_applicable'; not_applicable_reason?: string | null; feedback_revision: number; teacher_fields: FbField[];
    accepted_proposal_id: string | null; approved_by: string | null; approved_at: string | null; basis_stale: string | null } & Record<FbField, string | null>) | null;
  target: 'draft' | 'proposal' | null;
  approve_block: string | null; bulk_block: string | null;
}
export interface FeedbackView { assessment: { id: string; title: string; criterion: string; year_level: number }; rows: FeedbackRowView[] }

/** SOP §29 Teacher Verification Status. */
export function verification(r: JudgementRowView): { label: string; bg: string } {
  const j = r.judgement;
  if (!r.ready && r.attendance !== 'present') return { label: r.attendance === 'absent' ? 'ABSENT' : r.attendance === 'catchup' ? 'CATCH-UP PENDING' : 'EXEMPT', bg: '#e2e3e6' };
  if (j?.status === 'confirmed' && j.disposition === 'not_assessable') return { label: 'NOT ASSESSABLE', bg: '#d9dce1' };
  if (j?.status === 'confirmed') return { label: 'TEACHER CONFIRMED', bg: '#b9e6c4' };
  if (j?.status === 'needs_review') return { label: 'NEEDS RE-CHECK', bg: '#fddcbf' };
  if (j?.status === 'draft') return { label: 'DRAFT, NOT CONFIRMED', bg: '#fde9b8' };
  if (r.proposal?.status === 'pending') return { label: 'AI RECOMMENDED', bg: '#dbe7fb' };
  return { label: r.ready ? 'Ready for an AI level' : 'Not ready yet', bg: '#e2e3e6' };
}

/** Ready for a new AI draft: nothing open, or an AI-only draft that needs re-checking (regeneration). */
export function canDraft(r: FeedbackRowView): boolean {
  if (!r.ready) return false;
  if (!r.target) return r.feedback?.status !== 'approved';
  return r.target === 'draft' && r.feedback?.status === 'needs_review' && r.feedback.teacher_fields.length === 0;
}

/** What approving would put in front of the parent: the draft row, else the AI proposal. */
export function shownText(r: FeedbackRowView): Record<FbField, string | null> | null {
  if (r.target === 'draft' && r.feedback) return r.feedback;
  if (r.target === 'proposal' && r.proposal) return r.proposal;
  return r.feedback ?? r.proposal ?? null;
}

export function feedbackStatus(r: FeedbackRowView): { label: string; bg: string } {
  if (r.feedback?.status === 'not_applicable' && r.target === null) return { label: 'Not applicable', bg: '#d9dce1' };
  if (r.feedback?.status === 'approved' && r.target === null) return { label: 'TEACHER APPROVED', bg: '#b9e6c4' };
  if (r.target === 'proposal') return { label: 'DRAFT, NOT APPROVED', bg: '#fde9b8' };
  if (r.feedback?.status === 'needs_review') return { label: 'NEEDS RE-CHECK', bg: '#fddcbf' };
  if (r.target) return { label: 'DRAFT, NOT APPROVED', bg: '#fde9b8' };
  return { label: r.ready ? 'Ready to draft' : `Not ready yet${r.reasons.length ? ` (${r.reasons[0]})` : ''}`, bg: '#e2e3e6' };
}
